<template>
  <v-card class="member-card pa-4" outlined rounded="xl" :style="themeStyle">
    <v-row no-gutters align="start">
      <v-avatar
        size="64"
        class="mr-4"
        :color="student.avatar ? '' : theme.primary"
      >
        <v-img
          v-if="student.avatar"
          :src="student.avatar"
          cover
          class="member-card__avatar-image"
        />
        <span v-else class="white--text font-weight-bold">
          {{ initials }}
        </span>
      </v-avatar>

      <v-col>
        <div class="d-flex justify-space-between align-start flex-wrap gap-2">
          <div>
            <div class="member-card__name">
              {{ student.name || $t("memberCard.unnamed") }}
            </div>
            <div class="caption grey--text text--darken-1 mt-1">
              <v-icon small class="mr-1" :color="theme.secondary">
                mdi-email-outline
              </v-icon>
              {{ student.email || $t("memberCard.noEmail") }}
            </div>
          </div>

          <v-chip x-small dark :color="statusColor">
            {{ statusLabel }}
          </v-chip>
        </div>

        <div v-if="student.bio" class="caption mt-3 member-card__bio">
          {{ student.bio }}
        </div>
      </v-col>
    </v-row>

    <div class="mt-4 d-flex flex-wrap">
      <v-chip small outlined class="mr-2 mb-2" :color="theme.primary">
        <v-icon small left>mdi-chart-bar</v-icon>
        {{ levelLabel }}
      </v-chip>

      <v-chip small outlined class="mr-2 mb-2" :color="theme.secondary">
        <v-icon small left>mdi-refresh</v-icon>
        {{ frequencyLabel }}
      </v-chip>

      <v-chip small outlined class="mb-2" :color="theme.primary">
        <v-icon small left>mdi-account-group-outline</v-icon>
        {{ formatLabel }}
      </v-chip>
    </div>

    <div v-if="showLevelEditor" class="mt-1">
      <v-btn
        small
        text
        rounded
        class="member-card__edit-level px-0"
        :color="theme.primary"
        :loading="loadingLevelUpdate"
        @click="openLevelDialog"
      >
        <v-icon small left>mdi-pencil-outline</v-icon>
        {{ $t("memberCard.changeLevel") }}
      </v-btn>
    </div>

    <v-card v-if="showBilling" flat class="member-card__billing mt-4 pa-3">
      <div class="caption text-uppercase font-weight-bold grey--text">
        {{ $t("memberCard.monthlyFee") }}
      </div>
      <div class="member-card__billing-value">
        {{ billingValue || $t("memberCard.toBeAgreed") }}
      </div>
      <div
        v-if="billingDescription"
        class="caption mt-1 member-card__billing-description"
      >
        {{ billingDescription }}
      </div>

      <div v-if="showBillingEditor" class="mt-4">
        <v-select
          v-model="selectedBillingChoice"
          :items="billingChoices"
          item-text="text"
          item-value="value"
          :label="$t('memberCard.planLabel')"
          outlined
          dense
          hide-details="auto"
        />

        <v-text-field
          v-if="isCustomBillingSelected"
          v-model="customBillingValue"
          class="mt-3"
          :label="$t('memberCard.customValue')"
          prefix="R$"
          outlined
          dense
          hide-details="auto"
        />

        <div class="d-flex justify-end mt-3">
          <v-btn
            small
            rounded
            :color="theme.primary"
            class="white--text"
            :loading="loadingBillingUpdate"
            :disabled="!canSaveBilling"
            @click="saveBillingUpdate"
          >
            {{ $t("memberCard.saveBilling") }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Card 86aje36nz (retorno de 30/09): agendada a experimental, o
         "Agendar" sai. Mudar o dia é pelas opções do evento na agenda. -->
    <div
      v-if="showActions && experimental && trialScheduledLabel"
      class="caption mt-4 member-card__trial-info"
    >
      <v-icon small color="#b5421e" class="mr-1">mdi-flask-outline</v-icon>
      {{ $t("memberCard.trialInfo", { when: trialScheduledLabel }) }}
    </div>

    <v-card-actions v-if="showActions" class="px-0 pt-4 pb-0 flex-wrap">
      <v-btn
        v-if="experimental && student.status === 0 && !trialScheduledAt"
        class="mr-2 mb-2"
        outlined
        rounded
        color="#b5421e"
        :loading="loadingTrial"
        @click="openTrialDialog"
      >
        <v-icon small left>mdi-flask-outline</v-icon>
        {{ $t("memberCard.scheduleTrial") }}
      </v-btn>

      <v-btn
        v-if="student.status === 0"
        class="mr-2 mb-2"
        dark
        rounded
        :color="theme.primary"
        :loading="loadingSave"
        @click="openDialog(student.id)"
      >
        <v-icon small left>mdi-check-circle-outline</v-icon>
        {{
          experimental
            ? $t("memberCard.linkStudent")
            : $t("memberCard.acceptStudent")
        }}
      </v-btn>

      <v-btn
        v-if="student.status === 0"
        class="mb-2"
        outlined
        rounded
        color="error"
        :loading="loadingReject"
        @click="reject(student.id)"
      >
        <v-icon small left>mdi-close-circle-outline</v-icon>
        {{ $t("memberCard.rejectStudent") }}
      </v-btn>

      <v-btn
        v-if="student.status === 1"
        class="mr-2 mb-2"
        outlined
        rounded
        :color="theme.primary"
        :loading="loadingTransfer"
        @click="openTransfer"
      >
        <v-icon small left>mdi-swap-horizontal</v-icon>
        {{ $t("memberCard.transfer") }}
      </v-btn>

      <v-btn
        v-if="student.status === 1"
        class="mb-2"
        outlined
        rounded
        color="error"
        :loading="loadingReject"
        @click="removeDialog = true"
      >
        <v-icon small left>mdi-delete-outline</v-icon>
        {{ $t("memberCard.remove") }}
      </v-btn>
    </v-card-actions>

    <!-- Card 17trt6jda8c: trocar o aluno para outra turma do professor. Só
         aparecem turmas no nível do aluno e com vaga; o horário da turma nova
         é o que passa a valer, e por isso a troca pede confirmação. -->
    <v-dialog v-if="showActions" v-model="transferDialog" max-width="560">
      <v-card>
        <v-card-title class="text-h6">
          {{
            $t("memberCard.transferTitle", {
              name: student.name || $t("common.theStudent"),
            })
          }}
        </v-card-title>

        <v-card-text>
          <page-skeleton
            v-if="loadingTransferOptions"
            variant="rows"
            :rows="3"
          />

          <template v-else-if="!transferTarget">
            <v-alert dense text type="info" class="mb-3">
              {{ $t("memberCard.transferInfo", { level: transferLevelLabel }) }}
            </v-alert>

            <div v-if="!transferOptions.length" class="caption">
              {{ $t("memberCard.transferNone") }}
            </div>

            <v-radio-group
              v-else
              v-model="transferSelected"
              class="mt-0"
              hide-details
            >
              <v-radio
                v-for="option in transferOptions"
                :key="option.id"
                :value="option.id"
                color="#A826E1"
                class="transfer-option"
              >
                <template #label>
                  <div>
                    <div class="font-weight-bold">
                      {{
                        $t("memberCard.transferOption", {
                          code: option.cod,
                          format: formatLabelOf(option.format),
                          frequency: frequencyLabelOf(option.frequency),
                        })
                      }}
                    </div>
                    <div class="caption">
                      {{ describeSchedules(option.schedules) }}
                    </div>
                    <div class="caption grey--text text--darken-1">
                      {{
                        $t("memberCard.occupied", {
                          occupied: option.occupied,
                          capacity: option.capacity,
                        })
                      }}<span v-if="option.students.length">
                        · {{ option.students.join(", ") }}</span
                      >
                    </div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </template>

          <template v-else>
            <div class="mb-3">
              <i18n path="memberCard.goesTo" tag="span">
                <template #name>{{
                  student.name || $t("common.TheStudent")
                }}</template>
                <template #code>
                  <strong>{{ transferTarget.cod }}</strong>
                </template>
              </i18n>
            </div>
            <ul class="remove-impacts">
              <li>
                <strong>{{ $t("memberCard.newSchedule") }}</strong>
                {{ describeSchedules(transferTarget.schedules) }}.
                <span v-if="transferScheduleChanges">
                  {{
                    $t("memberCard.currentlyStudies", {
                      schedule: describeSchedules(transferCurrent.schedules),
                    })
                  }}
                </span>
              </li>
              <li>
                {{ $t("memberCard.transferLessons") }}
              </li>
              <li>
                {{ $t("memberCard.transferFee") }}
                <span v-if="transferPlanChanges">
                  {{ $t("memberCard.transferPlanChange") }}
                </span>
              </li>
              <li v-if="transferCurrent && transferCurrent.closesAfterTransfer">
                <strong>
                  {{ $t("memberCard.transferCloses") }}
                </strong>
              </li>
              <li>{{ $t("memberCard.transferNotify") }}</li>
            </ul>
            <div class="mt-3">{{ $t("memberCard.transferConfirmQ") }}</div>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="loadingTransfer"
            @click="transferTarget ? (transferTarget = null) : closeTransfer()"
          >
            {{ transferTarget ? $t("common.back") : $t("common.cancel") }}
          </v-btn>
          <v-btn
            v-if="!transferTarget"
            dark
            rounded
            :color="theme.primary"
            :disabled="!transferSelected"
            @click="reviewTransfer"
          >
            {{ $t("common.continue") }}
          </v-btn>
          <v-btn
            v-else
            dark
            rounded
            :color="theme.primary"
            :loading="loadingTransfer"
            @click="confirmTransfer"
          >
            {{ $t("memberCard.confirmTransfer") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Card 17trt6jda6j: remover o aluno pede confirmação e mostra o que
         acontece depois, como a API faz em releaseStudentFromGroup. -->
    <v-dialog v-if="showActions" v-model="removeDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">
          {{
            $t("memberCard.removeTitle", {
              name: student.name || $t("common.theStudent"),
            })
          }}
        </v-card-title>
        <v-card-text>
          <div class="mb-2">{{ $t("memberCard.onConfirm") }}</div>
          <ul class="remove-impacts">
            <li>
              {{ $t("memberCard.removeLessons") }}
            </li>
            <li>
              {{ $t("memberCard.removeHistory") }}
            </li>
            <li>
              {{ $t("memberCard.removeCharges") }}
            </li>
            <li v-if="isLastActiveStudent">
              <strong>
                {{ $t("memberCard.removeLast") }}
              </strong>
            </li>
            <li v-else>
              {{ $t("memberCard.removeOthers") }}
            </li>
            <li>
              {{ $t("memberCard.removeNotify") }}
            </li>
          </ul>
          <div class="mt-3">
            {{ $t("memberCard.removeIrreversible") }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingReject" @click="removeDialog = false">
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn
            dark
            rounded
            color="error"
            :loading="loadingReject"
            @click="confirmRemove"
          >
            {{ $t("memberCard.remove") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Card 86aje36nz (retorno de 30/09): vincular o aluno da experimental
         define a grade da turma. Aparecem todos os horários em que os dois
         podem, e o professor escolhe quantos a frequência do aluno pede. -->
    <v-dialog v-if="showActions" v-model="linkDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">{{
          $t("memberCard.linkStudent")
        }}</v-card-title>
        <v-card-text>
          <div class="caption grey--text text--darken-1 mb-3">
            {{
              $t("memberCard.linkHint", {
                name: student.name || $t("common.theStudent"),
              })
            }}
          </div>
          <div class="caption mb-3">
            {{ $t("memberCard.studentFrequency") }}
            <strong>{{ linkFrequencyLabel }}</strong> ·
            {{ linkRequirementLabel }}
          </div>

          <page-skeleton v-if="loadingLinkOptions" variant="rows" :rows="2" />
          <template v-else>
            <v-checkbox
              v-for="slot in linkSlots"
              :key="slotKey(slot)"
              v-model="linkSelected"
              :value="slotKey(slot)"
              :label="linkSlotLabel(slot)"
              :disabled="linkSlotDisabled(slot)"
              dense
              hide-details
              class="mt-1"
            />
            <div v-if="!linkSlots.length" class="error--text mt-2">
              {{ $t("memberCard.noCommonSlot") }}
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingSave" @click="linkDialog = false">
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn
            dark
            rounded
            :color="theme.primary"
            :loading="loadingSave"
            :disabled="!linkSelectionValid"
            @click="linkStudent"
          >
            {{ $t("memberCard.linkStudent") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="trialDialog" max-width="440">
      <v-card>
        <v-card-title class="text-h6">{{
          $t("memberCard.scheduleTrial")
        }}</v-card-title>
        <v-card-text>
          <div class="caption grey--text text--darken-1 mb-3">
            {{
              $t("memberCard.trialHint", {
                name: student.name || $t("common.theStudent"),
              })
            }}
          </div>
          <!-- Card 86aje36nz: o aluno pede um dia específico no calendário;
               confirmando o horário dele, a aula cai nesse dia. -->
          <div v-if="trialDateLabel" class="caption mb-3">
            <i18n path="memberCard.trialRequested" tag="span">
              <template #date>
                <strong>{{ trialDateLabel }}</strong>
              </template>
            </i18n>
          </div>
          <v-radio-group v-model="trialSlot" hide-details class="mt-0">
            <v-radio
              v-for="(slot, i) in normalizedTimes"
              :key="`trial-${i}`"
              :value="slot"
              :label="trialSlotLabel(slot)"
            />
          </v-radio-group>
          <div v-if="!normalizedTimes.length" class="error--text mt-2">
            {{ $t("memberCard.noSlots") }}
          </div>
        </v-card-text>
        <v-card-actions class="flex-wrap">
          <!-- Card 86aje36nz: o professor pode não poder no dia pedido. Em vez
               de agendar por cima da escolha do aluno, ele sugere outra data e
               o aluno confirma. -->
          <v-btn
            text
            color="#b5421e"
            :disabled="loadingTrial || loadingSuggestion"
            @click="openSuggestDialog"
          >
            {{ $t("memberCard.suggestDate") }}
          </v-btn>
          <v-spacer />
          <v-btn text :disabled="loadingTrial" @click="trialDialog = false">
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn
            color="#b5421e"
            dark
            :loading="loadingTrial"
            :disabled="!trialSlot"
            @click="scheduleTrial"
          >
            {{ $t("memberCard.schedule") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <trial-schedule-dialog
      ref="suggestDialog"
      :title="$t('memberCard.suggestDate')"
      :confirm-label="$t('memberCard.suggestConfirm')"
      :availability="suggestionSlots"
      :blocked="suggestionBusy"
      :busy="loadingSuggestion"
      @confirmed="suggestTrial"
    />

    <v-dialog v-if="showLevelEditor" v-model="levelDialog" max-width="420">
      <v-card>
        <v-card-title class="text-h6">{{
          $t("memberCard.levelTitle")
        }}</v-card-title>
        <v-card-text>
          <div class="caption grey--text text--darken-1 mb-4">
            {{ $t("memberCard.levelHint") }}
          </div>

          <v-select
            v-model="selectedLevel"
            :items="levelOptions"
            item-text="text"
            item-value="value"
            :label="$t('memberCard.levelLabel')"
            outlined
            dense
            hide-details="auto"
          />

          <div class="mt-4">
            <div class="caption grey--text text--darken-1 mb-2">
              {{ $t("memberCard.cascadeHint") }}
            </div>
            <v-checkbox
              v-for="sibling in siblingStudents"
              :key="sibling.id"
              v-model="cascadeStudentIds"
              :label="sibling.name"
              :value="sibling.id"
              dense
              hide-details
              class="mt-0"
            />
            <v-checkbox
              v-model="cascadeGroup"
              :label="$t('memberCard.cascadeGroup')"
              dense
              hide-details
              class="mt-0"
            />
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="closeLevelDialog">{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn
            dark
            rounded
            :color="theme.primary"
            :loading="loadingLevelUpdate"
            :disabled="
              selectedLevel === null ||
              (+selectedLevel === +student.level &&
                cascadeStudentIds.length === 0 &&
                !cascadeGroup)
            "
            @click="saveLevelUpdate"
          >
            {{ $t("memberCard.saveLevel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import {
  ENGLISH_LEVELS,
  displayGroupFormat,
  displayGroupFrequency,
  displayGroupLevel,
  initialsFromName,
  participantStatusColor,
  participantStatusLabel,
  resolveBillingValue,
} from "@/shared/classPresentation";

import TrialScheduleDialog from "@/components/connect/partner/TrialScheduleDialog.vue";
import { parseSchoolWallClock } from "@/shared/schoolWallClock";
import { formatBRL } from "@/plugins/i18n";

export default {
  name: "ClassMemberCard",

  components: { TrialScheduleDialog },

  props: {
    student: {
      type: Object,
      required: true,
    },
    theme: {
      type: Object,
      required: true,
    },
    normalizedTimes: {
      type: Array,
      default: () => [],
    },
    // Data (YYYY-MM-DD) que o aluno escolheu ao pedir a experimental.
    trialDate: {
      type: String,
      default: null,
    },
    // Data do evento da experimental, quando já agendada. Horário de parede
    // da escola: "yyyy-MM-dd HH:mm" (ver parseSchoolWallClock).
    trialScheduledAt: {
      type: String,
      default: null,
    },
    // A experimental já aconteceu, decidido pela API no relógio da escola.
    // null (API antiga): a tela compara com o relógio do navegador.
    trialHappened: {
      type: Boolean,
      default: null,
    },
    classDays: {
      type: Array,
      default: () => [],
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showBilling: {
      type: Boolean,
      default: false,
    },
    showBillingEditor: {
      type: Boolean,
      default: false,
    },
    showLevelEditor: {
      type: Boolean,
      default: false,
    },
    partnerPlans: {
      type: Array,
      default: () => [],
    },
    levelOptions: {
      type: Array,
      default: () => ENGLISH_LEVELS,
    },
    groupStudents: {
      type: Array,
      default: () => [],
    },
    experimental: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loadingSave: false,
      loadingReject: false,
      loadingLevelUpdate: false,
      loadingBillingUpdate: false,
      levelDialog: false,
      trialDialog: false,
      trialSlot: null,
      loadingTrial: false,
      // Sugestão de nova data (card 86aje36nz): horários em que os dois podem.
      suggestionSlots: [],
      suggestionBusy: [],
      loadingSuggestion: false,
      removeDialog: false,
      // Troca de turma (card 17trt6jda8c).
      transferDialog: false,
      loadingTransferOptions: false,
      loadingTransfer: false,
      transferOptions: [],
      transferCurrent: null,
      transferLevel: null,
      transferSelected: null,
      transferTarget: null,
      // Vincular depois da experimental (retorno de 30/09).
      linkDialog: false,
      loadingLinkOptions: false,
      linkSlots: [],
      linkSelected: [],
      linkFrequency: null,
      linkRequired: 1,
      linkExact: true,
      selected: null,
      selectedLevel: null,
      selectedBillingChoice: "__none",
      customBillingValue: null,
      cascadeStudentIds: [],
      cascadeGroup: false,
    };
  },

  watch: {
    student: {
      immediate: true,
      deep: true,
      handler() {
        this.syncBillingForm();
      },
    },
    "student.level": {
      immediate: true,
      handler(level) {
        this.selectedLevel =
          level === undefined || level === null ? null : +level;
      },
    },
  },

  computed: {
    requestedTrialDay() {
      if (!this.trialDate) return null;
      const date = new Date(`${this.trialDate}T12:00:00`);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    trialDateLabel() {
      const date = this.requestedTrialDay;
      if (!date) return "";
      const pad = (value) => String(value).padStart(2, "0");
      return this.$t("formats.weekdayDayMonth", {
        weekday: this.weekDayName(date.getDay()),
        day: pad(date.getDate()),
        month: pad(date.getMonth() + 1),
      });
    },
    trialScheduledLabel() {
      if (!this.trialScheduledAt) return "";
      // Retorno de 08/10 (card 86aje36nz): a data da aula é horário de parede
      // da escola. `new Date(...)` lia o "Z" como UTC e o navegador em São
      // Paulo mostrava 3h antes ("realizada em 07/10 às 17:00" para a aula
      // das 20:00).
      const wall = parseSchoolWallClock(this.trialScheduledAt);
      if (!wall) return this.$t("memberCard.scheduled");
      const when = this.$t("memberCard.when", {
        day: wall.day,
        month: wall.month,
        hour: wall.hour,
        minute: wall.minute,
      });
      const happened =
        typeof this.trialHappened === "boolean"
          ? this.trialHappened
          : wall.local.getTime() <= Date.now();
      return happened
        ? this.$t("memberCard.happenedOn", { when })
        : this.$t("memberCard.scheduledFor", { when });
    },
    linkFrequencyLabel() {
      return displayGroupFrequency(this.linkFrequency);
    },
    linkRequirementLabel() {
      const n = this.linkRequired;
      if (!this.linkExact)
        return this.$t("memberCard.pickAtLeast", { count: n });
      return this.$tc("memberCard.pickExact", n, { count: n });
    },
    linkSelectionValid() {
      const count = this.linkSelected.length;
      return this.linkExact
        ? count === this.linkRequired
        : count >= this.linkRequired;
    },
    siblingStudents() {
      return (this.groupStudents || []).filter(
        (item) => item.id !== this.student.id
      );
    },
    // Pedidos em aberto também seguram a turma (a API só apaga a turma sem
    // nenhum vínculo ativo ou pendente).
    transferLevelLabel() {
      return displayGroupLevel(this.transferLevel ?? this.student.level);
    },
    transferScheduleChanges() {
      if (!this.transferTarget || !this.transferCurrent) return false;
      return (
        this.describeSchedules(this.transferTarget.schedules) !==
        this.describeSchedules(this.transferCurrent.schedules)
      );
    },
    transferPlanChanges() {
      if (!this.transferTarget || !this.transferCurrent) return false;
      return (
        +this.transferTarget.format !== +this.transferCurrent.format ||
        +this.transferTarget.frequency !== +this.transferCurrent.frequency
      );
    },
    isLastActiveStudent() {
      return !this.siblingStudents.some((item) =>
        [0, 1].includes(Number(item.status))
      );
    },
    themeStyle() {
      return {
        "--member-primary": this.theme.primary,
        "--member-secondary": this.theme.secondary,
        "--member-surface": this.theme.surfaceAlt || this.theme.surface,
        "--member-border": this.theme.border,
      };
    },
    initials() {
      return initialsFromName(this.student.name);
    },
    levelLabel() {
      return displayGroupLevel(this.student.level);
    },
    frequencyLabel() {
      return displayGroupFrequency(this.student.frequency);
    },
    formatLabel() {
      return displayGroupFormat(this.student.format);
    },
    statusLabel() {
      return participantStatusLabel(this.student.status);
    },
    statusColor() {
      return participantStatusColor(this.student.status);
    },
    billingValue() {
      return resolveBillingValue(this.student);
    },
    billingDescription() {
      if (this.student?.billing?.mode === "CUSTOM") {
        return this.$t("memberCard.customBilling");
      }

      if (this.student?.currentPlan) {
        return `${displayGroupFormat(
          this.student.currentPlan.format
        )} - ${displayGroupFrequency(this.student.currentPlan.frequency)}`;
      }

      return this.$t("memberCard.noBilling");
    },
    billingChoices() {
      const plans = Array.isArray(this.partnerPlans) ? this.partnerPlans : [];
      const options = [
        ...plans.map((plan) => ({
          text: this.$t("memberCard.planOption", {
            format: displayGroupFormat(plan.format),
            frequency: displayGroupFrequency(plan.frequency),
            value: this.formatCurrencyValue(plan.finalValue ?? plan.value),
          }),
          value: plan.id,
        })),
      ];

      if (
        this.selectedBillingChoice &&
        !["__none", "__custom"].includes(this.selectedBillingChoice) &&
        !options.find((option) => option.value === this.selectedBillingChoice)
      ) {
        options.splice(1, 0, {
          text: this.$t("memberCard.currentPlan"),
          value: this.selectedBillingChoice,
        });
      }

      return options;
    },
    currentBillingChoice() {
      if (this.student?.billing?.mode === "CUSTOM") {
        return "__custom";
      }

      return this.student?.billing?.teachingPlanId || "__none";
    },
    currentCustomBillingValue() {
      if (this.student?.billing?.mode !== "CUSTOM") {
        return null;
      }

      return this.normalizeCurrencyInput(
        this.student?.billing?.customMonthlyValue
      );
    },
    isCustomBillingSelected() {
      return this.selectedBillingChoice === "__custom";
    },
    isBillingDirty() {
      if (this.selectedBillingChoice !== this.currentBillingChoice) {
        return true;
      }

      if (!this.isCustomBillingSelected) {
        return false;
      }

      return (
        this.normalizeCurrencyInput(this.customBillingValue) !==
        this.currentCustomBillingValue
      );
    },
    canSaveBilling() {
      if (!this.showBillingEditor || this.loadingBillingUpdate) {
        return false;
      }

      if (this.isCustomBillingSelected) {
        return (
          this.normalizeCurrencyInput(this.customBillingValue) !== null &&
          this.isBillingDirty
        );
      }

      return this.isBillingDirty;
    },
  },

  methods: {
    formatCurrencyValue(value) {
      const numericValue = Number(value);

      return formatBRL(Number.isNaN(numericValue) ? 0 : numericValue);
    },
    normalizeCurrencyInput(value) {
      if (value === undefined || value === null || value === "") {
        return null;
      }

      const rawValue = String(value).trim();
      const sanitizedValue = rawValue.includes(",")
        ? rawValue.replace(/\./g, "").replace(",", ".")
        : rawValue;
      const normalizedValue = Number(sanitizedValue);

      if (Number.isNaN(normalizedValue) || normalizedValue <= 0) {
        return null;
      }

      return Number(normalizedValue.toFixed(2));
    },
    syncBillingForm() {
      this.selectedBillingChoice = this.currentBillingChoice;
      this.customBillingValue =
        this.currentCustomBillingValue === null
          ? null
          : `${this.currentCustomBillingValue}`;
    },
    openLevelDialog() {
      this.selectedLevel =
        this.student.level === undefined || this.student.level === null
          ? null
          : +this.student.level;
      this.cascadeStudentIds = [];
      this.cascadeGroup = false;
      this.levelDialog = true;
    },
    closeLevelDialog() {
      this.levelDialog = false;
      this.selectedLevel =
        this.student.level === undefined || this.student.level === null
          ? null
          : +this.student.level;
    },
    // Card 86akj7x9f: aceitar insere o aluno direto na turma. A escolha de
    // horários não existe mais aqui — quem manda é a grade da turma, e a API
    // já ignorava os horários enviados neste passo. A turma experimental
    // continua com diálogo: lá o professor escolhe a grade recorrente.
    openDialog(selected) {
      this.selected = selected;

      if (this.experimental) {
        this.openLinkDialog();
        return;
      }

      this.admit([]);
    },
    slotKey(slot) {
      return `${slot.weekDay}|${slot.from}|${slot.to}`;
    },
    linkSlotLabel(slot) {
      return `${this.weekDayName(slot.weekDay)} · ${slot.from} – ${slot.to}`;
    },
    // Com a quantidade exata já marcada, os demais ficam travados; dá para
    // desmarcar e trocar. 3x+ aceita mais que o mínimo.
    linkSlotDisabled(slot) {
      return (
        this.linkExact &&
        this.linkSelected.length >= this.linkRequired &&
        !this.linkSelected.includes(this.slotKey(slot))
      );
    },
    async openLinkDialog() {
      this.linkDialog = true;
      this.linkSelected = [];
      this.loadingLinkOptions = true;

      try {
        const res = await partnerApi.getExperimentalLinkOptions(
          this.$route.params.id
        );
        this.linkSlots = res.data?.slots || [];
        this.linkFrequency = res.data?.frequency ?? null;
        this.linkRequired = Number(res.data?.requiredSlots) || 1;
        this.linkExact = res.data?.exact !== false;
      } catch (err) {
        this.linkDialog = false;
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("memberCard.loadSlotsFailed"),
          type: 1,
        });
      } finally {
        this.loadingLinkOptions = false;
      }
    },
    async linkStudent() {
      if (!this.linkSelectionValid) return;

      const schedules = this.linkSlots
        .filter((slot) => this.linkSelected.includes(this.slotKey(slot)))
        .map((slot) => ({
          weekDay: slot.weekDay,
          from: slot.from,
          to: slot.to,
        }));

      this.loadingSave = true;

      try {
        await partnerApi.updateRequest(this.$route.params.id, {
          customer: this.selected,
          status: 1,
          schedules,
        });
        this.$root.$emit("display-alert", {
          text: this.$t("memberCard.linked"),
          type: 0,
        });
        this.linkDialog = false;
        this.$emit("reload");
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message || this.$t("common.unexpectedError"),
          type: 1,
        });
      } finally {
        this.loadingSave = false;
      }
    },
    async saveLevelUpdate() {
      const levelUnchanged = +this.selectedLevel === +this.student.level;
      const hasCascade = this.cascadeStudentIds.length > 0 || this.cascadeGroup;
      if (this.selectedLevel === null || (levelUnchanged && !hasCascade)) {
        return;
      }

      this.loadingLevelUpdate = true;

      return await partnerApi
        .updateStudentEnglishLevel(this.$route.params.id, this.student.id, {
          level: +this.selectedLevel,
          studentIds: [...this.cascadeStudentIds],
          updateGroup: this.cascadeGroup,
        })
        .then(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("memberCard.levelUpdated"),
            type: 0,
          });
          this.closeLevelDialog();
          this.$emit("reload");
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("memberCard.levelUpdateFailed"),
            type: 1,
          });
        })
        .finally(() => {
          this.loadingLevelUpdate = false;
        });
    },
    async saveBillingUpdate() {
      let payload = {
        mode: "NONE",
      };

      if (this.isCustomBillingSelected) {
        const customMonthlyValue = this.normalizeCurrencyInput(
          this.customBillingValue
        );

        if (customMonthlyValue === null) {
          this.$root.$emit("display-alert", {
            text: this.$t("memberCard.invalidCustom"),
            type: 1,
          });
          return;
        }

        payload = {
          mode: "CUSTOM",
          customMonthlyValue,
        };
      } else if (this.selectedBillingChoice !== "__none") {
        payload = {
          mode: "PLAN",
          teachingPlanId: this.selectedBillingChoice,
        };
      }

      this.loadingBillingUpdate = true;

      return await partnerApi
        .updateStudentBilling(this.$route.params.id, this.student.id, payload)
        .then(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("memberCard.billingUpdated"),
            type: 0,
          });
          this.$emit("reload");
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("memberCard.billingUpdateFailed"),
            type: 1,
          });
        })
        .finally(() => {
          this.loadingBillingUpdate = false;
        });
    },
    async admit(weekDay) {
      this.loadingSave = true;

      return await partnerApi
        .updateRequest(this.$route.params.id, {
          customer: this.selected,
          status: 1,
          weekDay,
        })
        .then(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("memberCard.accepted"),
            type: 0,
          });
          this.$emit("reload");
        })
        .catch((err) => {
          // A API recusa o aceite com o motivo (ex.: turma já completa) --
          // mostrar o motivo em vez do "erro inesperado".
          this.$root.$emit("display-alert", {
            text:
              err?.response?.data?.message || this.$t("common.unexpectedError"),
            type: 1,
          });
        })
        .finally(() => {
          this.loadingSave = false;
        });
    },
    formatLabelOf(format) {
      return displayGroupFormat(format);
    },
    frequencyLabelOf(frequency) {
      return displayGroupFrequency(frequency);
    },
    describeSchedules(schedules) {
      const list = Array.isArray(schedules) ? schedules : [];
      if (!list.length) return this.$t("memberCard.noScheduleLower");
      return list
        .map(
          (slot) =>
            `${this.weekDayName(slot.weekDay)} ${slot.startHour} – ${
              slot.endHour
            }`
        )
        .join(", ");
    },
    async openTransfer() {
      this.transferDialog = true;
      this.transferSelected = null;
      this.transferTarget = null;
      this.transferOptions = [];
      this.loadingTransferOptions = true;

      try {
        const res = await partnerApi.studentTransferOptions(
          this.$route.params.id,
          this.student.id
        );
        this.transferOptions = res.data?.options || [];
        this.transferCurrent = res.data?.current || null;
        this.transferLevel = res.data?.studentLevel ?? null;
      } catch (err) {
        this.transferDialog = false;
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("memberCard.loadClassesFailed"),
          type: 1,
        });
      } finally {
        this.loadingTransferOptions = false;
      }
    },
    closeTransfer() {
      this.transferDialog = false;
      this.transferTarget = null;
    },
    reviewTransfer() {
      this.transferTarget =
        this.transferOptions.find(
          (option) => option.id === this.transferSelected
        ) || null;
    },
    async confirmTransfer() {
      if (!this.transferTarget) return;

      this.loadingTransfer = true;

      try {
        const res = await partnerApi.transferStudent(
          this.$route.params.id,
          this.student.id,
          this.transferTarget.id
        );
        this.$root.$emit("display-alert", {
          text: this.$t("memberCard.transferred", {
            code: this.transferTarget.cod,
          }),
          type: 0,
        });
        this.transferDialog = false;

        // A turma atual pode ter sido encerrada: segue para a nova.
        const target = res.data?.toClassId || this.transferTarget.id;
        this.$router.push(`/connect/professor/turmas/${target}`);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("memberCard.transferFailed"),
          type: 1,
        });
      } finally {
        this.loadingTransfer = false;
      }
    },
    async confirmRemove() {
      await this.reject(this.student.id);
      this.removeDialog = false;
    },
    async reject(id) {
      this.loadingReject = true;

      return await partnerApi
        .updateRequest(this.$route.params.id, {
          customer: id,
          status: 3,
        })
        .then(() => {
          this.$root.$emit("display-alert", {
            text:
              Number(this.student.status) === 1
                ? this.$t("memberCard.removed")
                : this.$t("memberCard.rejected"),
            type: 1,
          });
          this.$emit("reload");
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("common.unexpectedError"),
            type: 1,
          });
        })
        .finally(() => {
          this.loadingReject = false;
        });
    },
    weekDayName(weekDay) {
      const day = +weekDay;
      return day >= 0 && day <= 6 ? this.$t(`enums.weekDay.${day}`) : "-";
    },
    isRequestedTrialSlot(slot) {
      return (
        !!this.requestedTrialDay &&
        +slot.weekDay === this.requestedTrialDay.getDay()
      );
    },
    trialSlotLabel(slot) {
      const base = `${this.weekDayName(slot.weekDay)} · ${slot.start} – ${
        slot.end
      }`;
      return this.isRequestedTrialSlot(slot)
        ? this.$t("memberCard.requestedDay", {
            base,
            date: this.trialDateLabel,
          })
        : base;
    },
    openTrialDialog() {
      // Já abre no horário que o aluno pediu, quando ele existe na lista.
      this.trialSlot =
        this.normalizedTimes.find((slot) => this.isRequestedTrialSlot(slot)) ||
        this.normalizedTimes[0] ||
        null;
      this.trialDialog = true;
    },
    async scheduleTrial() {
      if (!this.trialSlot) return;
      this.loadingTrial = true;

      return await partnerApi
        .acceptExperimentalTrial(this.$route.params.id, {
          customerId: this.student.id,
          weekDay: this.trialSlot.weekDay,
          from: this.trialSlot.start,
          to: this.trialSlot.end,
        })
        .then(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("memberCard.trialScheduled"),
            type: 0,
          });
          this.trialDialog = false;
          this.$emit("reload");
        })
        .catch((err) => {
          const message =
            err?.response?.data?.message || this.$t("memberCard.trialFailed");
          this.$root.$emit("display-alert", { text: message, type: 1 });
        })
        .finally(() => {
          this.loadingTrial = false;
        });
    },
    // Card 86aje36nz: as opções vêm do servidor, cruzando a disponibilidade do
    // professor com a do aluno — o mesmo cruzamento que o aluno viu ao pedir.
    async openSuggestDialog() {
      this.loadingSuggestion = true;

      try {
        const res = await partnerApi.getExperimentalSlotOptions(
          this.$route.params.id
        );
        this.suggestionSlots = res.data?.slots || [];
        this.suggestionBusy = res.data?.busy || [];

        if (!this.suggestionSlots.length) {
          this.$root.$emit("display-alert", {
            text: this.$t("memberCard.noCommonFree"),
            type: 1,
          });
          return;
        }

        this.trialDialog = false;
        this.$refs.suggestDialog.open();
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("memberCard.loadSlotsFailed"),
          type: 1,
        });
      } finally {
        this.loadingSuggestion = false;
      }
    },
    async suggestTrial(hours) {
      const [slot] = hours || [];
      if (!slot) return;

      this.loadingSuggestion = true;

      try {
        await partnerApi.suggestExperimentalTrial(this.$route.params.id, {
          date: slot.date,
          from: slot.from,
          to: slot.to,
        });
        this.$refs.suggestDialog.close();
        this.$root.$emit("display-alert", {
          text: this.$t("memberCard.suggested"),
          type: 0,
        });
        this.$emit("reload");
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message || this.$t("memberCard.suggestFailed"),
          type: 1,
        });
      } finally {
        this.loadingSuggestion = false;
      }
    },
  },
};
</script>

<style scoped lang="css">
/* O reset global (styles/base/_reset.scss) tira o marcador de ul e li;
   aqui os impactos precisam dele (retorno de 16/10). */
.remove-impacts {
  padding-left: 20px;
}

.remove-impacts li {
  list-style: disc outside;
}

.remove-impacts li::marker {
  color: var(--dk-grape, #a826e1);
}

.remove-impacts li + li {
  margin-top: 6px;
}

.member-card {
  background: var(--dk-surface, #ffffff);
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09) !important;
  border-radius: 20px !important;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}

.member-card__name {
  color: var(--dk-ink, #231a2e);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
}

.member-card__bio {
  color: var(--dk-muted, #6b6472);
  line-height: 1.5;
}

.member-card__avatar-image {
  width: 100%;
  height: 100%;
}

.member-card__billing {
  background: var(--dk-paper, #faf7fc);
  border-radius: 16px;
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
}

.member-card__billing-value {
  color: var(--member-primary);
  font-size: 1.15rem;
  font-weight: 800;
}

.member-card__billing-description {
  color: var(--dk-muted, #6b6472);
  line-height: 1.45;
}

.member-card__trial-info {
  color: var(--dk-ember-deep, #b5421e);
}

.member-card__edit-level {
  text-transform: none;
}

.gap-2 {
  gap: 8px;
}
</style>
